import React, { useState } from 'react';

import { queryCache, useMutation, useQuery } from 'react-query';
import { Toast } from 'src/common/components';

import {
  archiveAccountCohort,
  createAccountCohort,
  fetchAccountCohortActiveAccounts,
  patchAccountCohort,
} from '../api';
import {
  ACCOUNT_COHORT_ACTIVE_ACCOUNTS_QUERY_KEY,
  ACCOUNT_COHORT_MODE_LABEL,
  ACCOUNT_COHORT_PREVIEW_QUERY_KEY,
  ACCOUNT_COHORTS_QUERY_KEY,
  COHORT_ATTRIBUTE_LABEL,
} from '../constants';
import {
  AccountCohort,
  AccountCohortPayload,
  CohortAttribute,
  CohortMode,
  SelectedAccount,
} from '../types';
import { getCohortErrorMessage } from '../utils';

import AccountCohortPreview from './AccountCohortPreview';
import CohortAccountSelector from './CohortAccountSelector';

type AccountCohortFormProps = {
  cohort?: AccountCohort;
  onSaved: (cohort: AccountCohort) => void;
  onArchived: () => void;
};

const AccountCohortForm: React.FC<AccountCohortFormProps> = ({ cohort, onSaved, onArchived }) => {
  const isEditing = !!cohort;
  const isProtected = !!cohort?.is_protected;

  const [name, setName] = useState(cohort?.name ?? '');
  const [description, setDescription] = useState(cohort?.description ?? '');
  const [mode, setMode] = useState<CohortMode | ''>(cohort?.mode ?? '');
  const [attribute, setAttribute] = useState<CohortAttribute>(
    cohort?.attribute ?? 'waitinglist_v2'
  );
  const [excludedAccountIds, setExcludedAccountIds] = useState<string[]>(
    cohort?.excluded_account_ids ?? []
  );
  const [selectedAccounts, setSelectedAccounts] = useState<SelectedAccount[]>(
    cohort?.mode === 'manual' ? cohort.account_ids.map((id) => ({ id, name: `Cuenta #${id}` })) : []
  );
  const [confirmingArchive, setConfirmingArchive] = useState(false);

  const {
    data: activeAccountsData,
    isLoading: isLoadingActiveAccounts,
    isError: isActiveAccountsError,
    isSuccess: isActiveAccountsSuccess,
    refetch: refetchActiveAccounts,
  } = useQuery([ACCOUNT_COHORT_ACTIVE_ACCOUNTS_QUERY_KEY], fetchAccountCohortActiveAccounts, {
    enabled: mode === 'all' && !isProtected,
  });

  // The cohort only stores account IDs; fill in real names from the preview sample.
  const fillAccountNames = (sample: SelectedAccount[]): void => {
    const namesById = new Map(sample.map((a) => [a.id, a.name]));
    setSelectedAccounts((prev) =>
      prev.map((a) => (namesById.has(a.id) ? { id: a.id, name: namesById.get(a.id) ?? a.name } : a))
    );
  };

  const buildPayload = (): AccountCohortPayload | null => {
    if (mode === '') {
      return null;
    }
    const base = { name: name.trim(), description: description.trim(), mode };
    if (mode === 'all') {
      return { ...base, excluded_account_ids: excludedAccountIds };
    }
    if (mode === 'automatic') {
      return { ...base, attribute };
    }
    return { ...base, account_ids: selectedAccounts.map((a) => a.id) };
  };

  const [save, { isLoading: isSaving }] = useMutation(
    () => {
      if (cohort && cohort.is_protected) {
        return patchAccountCohort(cohort.id, { description: description.trim() });
      }
      const payload = buildPayload();
      if (!payload) {
        return Promise.reject(new Error('missing mode'));
      }
      return cohort ? patchAccountCohort(cohort.id, payload) : createAccountCohort(payload);
    },
    {
      onSuccess: (res) => {
        void queryCache.invalidateQueries(ACCOUNT_COHORTS_QUERY_KEY);
        void queryCache.invalidateQueries([ACCOUNT_COHORT_PREVIEW_QUERY_KEY, res.data.id]);
        void queryCache.invalidateQueries(ACCOUNT_COHORT_ACTIVE_ACCOUNTS_QUERY_KEY);
        Toast.success(isEditing ? 'Cohort de cuentas actualizado.' : 'Cohort de cuentas creado.');
        onSaved(res.data);
      },
      onError: (error) => {
        Toast.error(
          getCohortErrorMessage(
            error,
            isEditing
              ? 'No se pudo guardar el cohort de cuentas.'
              : 'No se pudo crear el cohort de cuentas.'
          )
        );
      },
    }
  );

  const [archive, { isLoading: isArchiving }] = useMutation(
    () => archiveAccountCohort(cohort?.id ?? 0),
    {
      onSuccess: () => {
        void queryCache.invalidateQueries(ACCOUNT_COHORTS_QUERY_KEY);
        Toast.success('Cohort de cuentas archivado.');
        onArchived();
      },
      onError: (error) => {
        setConfirmingArchive(false);
        Toast.error(getCohortErrorMessage(error, 'No se pudo archivar el cohort de cuentas.'));
      },
    }
  );

  const isSaveDisabled =
    isSaving ||
    (isProtected
      ? false
      : name.trim() === '' ||
        mode === '' ||
        (mode === 'all' && !isActiveAccountsSuccess) ||
        (mode === 'manual' && selectedAccounts.length === 0));

  return (
    <div className="max-w-xl p-6 space-y-4">
      <div className="flex items-center gap-2">
        <h2 className="flex-1 text-lg font-bold text-gray-900">
          {isEditing ? cohort.name : 'Nuevo cohort de cuentas'}
        </h2>
        {isProtected && (
          <span className="px-2 py-0.5 text-xs font-semibold text-blue-700 bg-blue-50 rounded-full">
            Protegido
          </span>
        )}
      </div>

      {cohort && <AccountCohortPreview cohortId={cohort.id} onSample={fillAccountNames} />}

      {isProtected && cohort ? (
        <p className="bg-gray-50 p-2 text-xs text-gray-500 rounded">
          {ACCOUNT_COHORT_MODE_LABEL[cohort.mode]}. Este cohort es protegido: solo puedes editar su
          descripción.
        </p>
      ) : (
        <label className="block">
          <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">Nombre</span>
          <input
            className="input mt-0.5 w-full text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
      )}

      <label className="block">
        <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
          Descripción
        </span>
        <textarea
          className="input mt-0.5 w-full text-sm"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      {!isProtected && (
        <CohortAccountSelector
          mode={mode}
          onModeChange={(nextMode) => {
            setMode(nextMode);
            setSelectedAccounts([]);
            setExcludedAccountIds(
              nextMode === 'all' && cohort?.mode === 'all' ? cohort.excluded_account_ids : []
            );
          }}
          excludedAccountIds={excludedAccountIds}
          onExcludedAccountIdsChange={setExcludedAccountIds}
          attribute={attribute}
          onAttributeChange={setAttribute}
          selectedAccounts={selectedAccounts}
          onSelectedAccountsChange={setSelectedAccounts}
          activeAccounts={{
            data: activeAccountsData?.data,
            isLoading: isLoadingActiveAccounts,
            isError: isActiveAccountsError,
            isSuccess: isActiveAccountsSuccess,
            refetch: refetchActiveAccounts,
          }}
          disabled={isSaving}
          modeLabel="Modo"
          modePlaceholder="Selecciona un modo…"
        />
      )}

      {!isEditing && (
        <p className="text-xs text-gray-400">El conteo de cuentas aparece después de guardar.</p>
      )}
      {cohort && !isProtected && cohort.mode === 'automatic' && cohort.attribute && (
        <p className="text-xs text-gray-400">
          Guardado: {COHORT_ATTRIBUTE_LABEL[cohort.attribute]}.
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          className="btn btn-primary px-4 py-2 text-sm"
          disabled={isSaveDisabled}
          onClick={() => save()}
        >
          {isSaving ? 'Guardando…' : 'Guardar'}
        </button>
        {cohort &&
          !isProtected &&
          (confirmingArchive ? (
            <span className="flex items-center gap-1 text-xs text-gray-600">
              ¿Archivar?
              <button
                type="button"
                className="btn btn-ghost px-2 py-1 text-xs text-red-600"
                disabled={isArchiving}
                onClick={() => archive()}
              >
                Sí, archivar
              </button>
              <button
                type="button"
                className="btn btn-ghost px-2 py-1 text-xs"
                onClick={() => setConfirmingArchive(false)}
              >
                No
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="btn btn-ghost px-3 py-1.5 text-xs text-red-600 border border-gray-200 rounded-full"
              onClick={() => setConfirmingArchive(true)}
            >
              Archivar
            </button>
          ))}
      </div>
    </div>
  );
};

export default AccountCohortForm;
